import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { SeguroDesempregoResultsType } from "../calculadora-seguro-desemprego";
import { CONSTANTES_SEGURO_DESEMPREGO_2025 } from "../calculadora-seguro-desemprego";

const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace(".", ",");
};

const Tooltip = ({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 max-w-xs text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-normal z-20 text-center">
      {text}
    </div>
  </div>
);

const CalculoBreakdown = ({
  results,
}: {
  results: SeguroDesempregoResultsType;
}) => {
  const { detalhamentoCalculo } = results;

  return (
    <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 text-xs">
      <h5 className="font-semibold text-blue-800 mb-2">
        Detalhamento do Cálculo:
      </h5>

      {/* Média Salarial */}
      <div className="mb-3">
        <p className="text-blue-700 font-medium mb-1">
          1. Cálculo da Média Salarial:
        </p>
        <p className="text-blue-700 mb-1">
          • Antepenúltimo: R${" "}
          {formatarMoedaPtBR(detalhamentoCalculo.salarios.antepenultimo)}
        </p>
        <p className="text-blue-700 mb-1">
          • Penúltimo: R${" "}
          {formatarMoedaPtBR(detalhamentoCalculo.salarios.penultimo)}
        </p>
        <p className="text-blue-700 mb-1">
          • Último: R$ {formatarMoedaPtBR(detalhamentoCalculo.salarios.ultimo)}
        </p>
        <p className="text-blue-700 font-medium">
          = Média: R$ {formatarMoedaPtBR(detalhamentoCalculo.salarios.media)}
        </p>
      </div>

      {/* Faixa Aplicada */}
      <div className="mb-3">
        <p className="text-blue-700 font-medium mb-1">
          2. {detalhamentoCalculo.faixaAplicada.descricao}:
        </p>
        <p className="text-blue-700 mb-1">
          {detalhamentoCalculo.faixaAplicada.limiteFaixa}
        </p>
        <p className="text-blue-700 mb-1">
          Cálculo: {detalhamentoCalculo.faixaAplicada.calculo}
        </p>

        {detalhamentoCalculo.faixaAplicada.valorBruto !==
          detalhamentoCalculo.faixaAplicada.valorFinal && (
          <p className="text-blue-700 font-medium">
            Aplicado piso mínimo: R${" "}
            {formatarMoedaPtBR(
              CONSTANTES_SEGURO_DESEMPREGO_2025.PISO_BENEFICIO,
            )}
          </p>
        )}
      </div>

      {/* Número de Parcelas */}
      <div className="mb-2">
        <p className="text-blue-700 font-medium mb-1">3. Número de Parcelas:</p>
        <p className="text-blue-700 mb-1">
          • {detalhamentoCalculo.numeroParcelas.baseHistorico}
        </p>
        <p className="text-blue-700 mb-1">
          • Meses trabalhados:{" "}
          {detalhamentoCalculo.numeroParcelas.mesesTrabalhados}
        </p>
        <p className="text-blue-700 font-medium">
          = Direito a {detalhamentoCalculo.numeroParcelas.parcelasFinal}{" "}
          parcelas
        </p>
      </div>
    </div>
  );
};

const CronogramaParcelas = ({
  results,
}: {
  results: SeguroDesempregoResultsType;
}) => {
  return (
    <div className="mt-4">
      <h4 className="font-semibold text-gray-700 mb-3 text-center">
        Cronograma de Parcelas
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {results.cronogramaParcelas.map((parcela) => (
          <div
            key={parcela.numero}
            className="p-3 bg-gray-50 rounded border text-center"
          >
            <p className="text-sm font-medium text-gray-700 mb-1">
              {parcela.numero}ª Parcela
            </p>
            <p className="text-lg font-bold text-gray-800">
              R$ {normalSanatizerBRL(parcela.valor)}
            </p>
            <p className="text-xs text-gray-600">{parcela.dataEstimada}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-600 mt-3 text-center">
        * Datas estimadas. O pagamento efetivo depende da aprovação e
        processamento pelo órgão competente.
      </p>
    </div>
  );
};

export const SeguroDesempregoResponseCard = ({
  results,
}: {
  results: SeguroDesempregoResultsType;
}) => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Se não elegível, mostrar apenas a mensagem
  if (!results.elegivel) {
    return (
      <MainCard className="mt-4">
        <MainCardTitle>Resultado da análise</MainCardTitle>

        <div className="p-4 bg-red-50 rounded border border-red-200 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-red-600 font-bold">✗</span>
            <h3 className="font-semibold text-red-800">
              Não Elegível para o Benefício
            </h3>
          </div>
          <p className="text-sm text-red-700">
            {results.motivoInelegibilidade}
          </p>
        </div>

        {/* Orientações */}
        <div className="p-4 bg-blue-50 rounded border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-2">O que fazer:</h3>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• Continue trabalhando até atingir o tempo mínimo necessário</p>
            <p>• Mantenha sua carteira de trabalho atualizada</p>
            <p>• Consulte as regras oficiais no site do Governo Federal</p>
            <p>• Procure orientação em uma agência do trabalhador</p>
          </div>
        </div>

        {/* Disclaimer simplificado */}
        <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Importante:</strong> Esta é uma simulação baseada nas regras
            vigentes. Para informações oficiais, acesse o portal Gov.br ou
            procure uma agência do trabalhador.
          </p>
        </div>
      </MainCard>
    );
  }

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado do Seguro-desemprego</MainCardTitle>

      {/* Cards de Resultado Principal */}
      <div className="grid sm:grid-cols-2 gap-6 mb-6 grid-cols-1 max-w-4xl mx-auto">
        <div className="p-4 bg-primary rounded border flex flex-col justify-center">
          <Tooltip text="Valor que você receberá mensalmente durante o período do benefício">
            <h2 className="sm:text-lg text-base text-center text-white font-semibold m-0 cursor-help">
              Valor da parcela
            </h2>
          </Tooltip>
          <p className="sm:text-2xl text-xl text-center font-bold text-white m-0">
            <span className="text-base mr-1 text-white">R$</span>
            {normalSanatizerBRL(results.valorParcela)}
          </p>
          <p className="text-sm text-center text-white opacity-90 mt-1">
            Por {results.numeroParcelas} meses
          </p>
        </div>

        <div className="p-4 bg-white rounded border flex flex-col justify-center border-2 border-red-200">
          <Tooltip text="Valor total que você receberá somando todas as parcelas">
            <h2 className="sm:text-lg text-base text-center font-semibold m-0 cursor-help">
              Valor total do benefício
            </h2>
          </Tooltip>
          <p className="sm:text-2xl text-xl text-center font-black text-red-800 m-0">
            <span className="text-base mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(results.valorTotal)}
          </p>
          <p className="text-sm text-center text-gray-600 mt-1">
            {results.numeroParcelas} parcelas × R${" "}
            {normalSanatizerBRL(results.valorParcela)}
          </p>
        </div>
      </div>

      {/* Status de Elegibilidade - SIMPLIFICADO */}
      <div className="mb-6 p-4 bg-green-50 rounded border border-green-200">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-green-600 font-bold">✓</span>
          <h3 className="font-semibold text-green-800">
            Você tem direito ao benefício!
          </h3>
        </div>
        <div className="text-sm text-green-700">
          <p className="mb-1">
            <strong>Salário médio:</strong> R${" "}
            {normalSanatizerBRL(results.salarioMedio)} |<strong> Faixa:</strong>{" "}
            {results.faixaAplicada} |<strong> Parcelas:</strong>{" "}
            {results.numeroParcelas}
          </p>
        </div>
      </div>

      {/* Detalhamento do Cálculo */}
      <div className="mb-6">
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full p-3 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 text-left transition-colors duration-200"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-800 text-sm">
              Como foi calculado o valor
            </h3>
            <span className="text-blue-600 text-sm">
              {showBreakdown ? "Ocultar" : "Ver Detalhes"}
            </span>
          </div>
        </button>

        {showBreakdown && <CalculoBreakdown results={results} />}
      </div>

      {/* Cronograma de Parcelas */}
      <CronogramaParcelas results={results} />
    </MainCard>
  );
};
